import { onMounted } from 'vue';
import { type LocationQuery, useRoute } from 'vue-router';
import { getInsightRecommendedActionPrefill } from '@/api/insights';
import type {
  InsightRecommendedActionAgentType,
  InsightRecommendedActionPrefill,
  InsightRecommendedActionPrefillInputMap,
} from '@/types/insights';
import { routeQueryToString } from '@/utils/routeParams';

export const INSIGHT_ACTION_UUID_QUERY_KEY = 'insight_action_uuid';
export const INSIGHT_ACTION_ID_QUERY_KEY = 'insight_action_id';

export const parseInsightActionId = (
  query: LocationQuery,
): number | undefined => {
  const rawId = routeQueryToString(query[INSIGHT_ACTION_ID_QUERY_KEY]);
  if (rawId === null) return undefined;

  const id = Number(rawId);
  return Number.isInteger(id) ? id : undefined;
};

const prefillInputFor = <T extends InsightRecommendedActionAgentType>(
  prefill: InsightRecommendedActionPrefill,
  agentType: T,
): InsightRecommendedActionPrefillInputMap[T] | null =>
  prefill.agent_type === agentType
    ? (prefill.input as InsightRecommendedActionPrefillInputMap[T] | null)
    : null;

export const useInsightActionPrefill = <
  T extends InsightRecommendedActionAgentType,
>(
  agentType: T,
  onPrefill: (
    input: InsightRecommendedActionPrefillInputMap[T],
  ) => void | Promise<void>,
) => {
  const route = useRoute();

  onMounted(async () => {
    const uuid = route.query[INSIGHT_ACTION_UUID_QUERY_KEY];
    if (typeof uuid !== 'string' || !uuid) return;

    const prefill = await getInsightRecommendedActionPrefill(uuid);
    const input = prefillInputFor(prefill, agentType);
    if (input === null) return;

    await onPrefill(input);
  });
};
